// Attribute designations (#11270, extended by #11392). A designation is what
// the system reads to find "the fund's manager field" / "the person's role
// field", independent of what the field is named. The wording is shared by
// the schema editor's read-only badge and the Edit Field modal's set control,
// so the two can never disagree.
//
// A person's role is ONE user-visible concept with two halves — the job title
// and the employer — which an organization may keep on one field or on two
// (#11392). How a field carries it follows from the field's shape:
//
//   - text AND links to companies or managers (the COMBINED shape): the field
//     carries "Role" whole. Once the role designation sits on such a field,
//     the role is fully declared and no other field is offered any part of it.
//   - text only: the field carries the title half, "Role - Title".
//   - links to companies or managers with no scalar of its own: the field
//     carries the employer half, "Role - Employer".
//
// "Employer" is never standalone vocabulary — the employer half is always
// named as part of the role. The stored tokens (`person_role`,
// `person_employer`, `person_email`, `person_phone`) are internal and never
// shown.
//
// Every designation but one follows the slot model below: one holder per
// organization and record type. `person_email` (#13069) and `person_phone`
// (#11277) have no slot: a person keeps addresses and numbers on several
// fields, every consumer reads the whole
// designated set, and so the designation is offered on every person text
// field however many already carry it. `document_password` (#14151) has no
// slot either: a manager, vehicle or company keeps its documents' passwords on
// any number of text fields, and every value is tried on a locked document.

import type {
  AttributeDesignation,
  EntityKind,
  SchemaFact,
} from "../Dashboard/types";

// The record types an employer link can point at. A person works for a
// company or for a manager; a field that can hold neither cannot hold the
// employer half, and the server refuses to designate one.
const EMPLOYER_KINDS: EntityKind[] = ["company", "manager"];

const linksToEmployer = (entityTypes: SchemaFact["entity_types"]): boolean =>
  entityTypes?.some((kind) => EMPLOYER_KINDS.includes(kind)) ?? false;

// The combined shape: one field holding both halves of the role.
const combinedRoleShape = (
  scalarType: SchemaFact["scalar_type"],
  entityTypes: SchemaFact["entity_types"]
): boolean => scalarType === "string" && linksToEmployer(entityTypes);

// Names what the field HOLDS, in the client's words, derived from the field's
// shape. Not "Fund Manager": an investment vehicle is not necessarily a fund.
// A text-only role field reads "Role - Title" even when no employer half is
// declared anywhere — the shape is what makes it half a role.
export const designationLabel = (
  designation: AttributeDesignation,
  scalarType: SchemaFact["scalar_type"],
  entityTypes: SchemaFact["entity_types"]
): string => {
  switch (designation) {
    case "fund_manager":
      return "Management Company";
    case "manages_vehicles":
      return "Managed Vehicles";
    case "person_role":
      return combinedRoleShape(scalarType, entityTypes)
        ? "Role"
        : "Role - Title";
    case "person_employer":
      return "Role - Employer";
    case "person_email":
      return "Email address";
    case "person_phone":
      return "Phone number";
    case "document_password":
      return "Document password";
  }
};

// Say what the value is. Nothing about which field the system reads or how
// designations resolve — that is internal machinery, not the client's concern.
export const designationTooltip = (
  designation: AttributeDesignation,
  scalarType: SchemaFact["scalar_type"],
  entityTypes: SchemaFact["entity_types"]
): string => {
  switch (designation) {
    case "fund_manager":
      return "The company that manages the investment vehicle.";
    case "manages_vehicles":
      return "The investment vehicles this company manages.";
    case "person_role":
      return combinedRoleShape(scalarType, entityTypes)
        ? "The person's current role: their job title and employer."
        : "The job-title half of the person's role.";
    case "person_employer":
      return "The employer half of the person's role.";
    case "person_email":
      return "An email address of the person.";
    case "person_phone":
      return "A phone number of the person.";
    case "document_password":
      return "The passwords this record's documents are protected with.";
  }
};

// The designations several attributes of one organization may carry at once.
// Setting one moves nothing: no other holder is cleared, here or on the
// server.
export const MULTI_HOLDER_DESIGNATIONS: ReadonlySet<AttributeDesignation> =
  new Set<AttributeDesignation>([
    "person_email",
    "person_phone",
    "document_password",
  ]);

// A mid-sentence noun for the modal's checkbox line — a lowercased label
// would read as "the role - title". A single-holder noun follows "the field
// holding the"; a multi-holder noun carries its own article, since the field
// is one of several holding it.
const designationNoun = (
  designation: AttributeDesignation,
  scalarType: SchemaFact["scalar_type"],
  entityTypes: SchemaFact["entity_types"]
): string => {
  switch (designation) {
    case "fund_manager":
      return "management company";
    case "manages_vehicles":
      return "managed vehicles";
    case "person_role":
      return combinedRoleShape(scalarType, entityTypes)
        ? "role"
        : "role's job title";
    case "person_employer":
      return "role's employer";
    case "person_email":
      return "an email address of the person";
    case "person_phone":
      return "a phone number of the person";
    case "document_password":
      // Never read: the checkbox line for this designation states what the
      // passwords do (below) rather than naming the value.
      return "the passwords that open this record's locked documents";
  }
};

// The line under a designation checkbox in the Edit Field panel, ticked or
// not. A single-holder designation names THE field holding it; a multi-holder
// one (#13069) cannot, since other fields may hold it too. The document
// password line (#14151) says what the passwords do instead of what the field
// holds: that consequence is the one thing the panel cannot show.
export const designationCheckboxLine = (
  designation: AttributeDesignation,
  checked: boolean,
  scalarType: SchemaFact["scalar_type"],
  entityTypes: SchemaFact["entity_types"]
): string => {
  if (designation === "document_password") {
    return checked
      ? "Locked documents of this record open with the passwords kept here."
      : "Mark this field as holding the passwords that open this record's locked documents.";
  }
  const noun = designationNoun(designation, scalarType, entityTypes);
  if (MULTI_HOLDER_DESIGNATIONS.has(designation)) {
    return checked
      ? `This field holds ${noun}.`
      : `Mark this field as holding ${noun}.`;
  }
  return checked
    ? `This is the field holding the ${noun}.`
    : `Mark this as the field holding the ${noun}.`;
};

// The record kind to create when a user creates a new record inline from this
// field's picker, or null when the field does not pin one down. A field
// carrying the management company holds a manager, so asking whether they
// meant a company is asking a question we already know the answer to.
//
// Guarded on the field actually accepting managers so a designation that
// somehow outlived a narrowing of `entity_types` can't preselect a kind the
// field can't hold.
export const designatedCreateKind = (
  designation: AttributeDesignation | null | undefined,
  entityTypes: SchemaFact["entity_types"]
): EntityKind | null =>
  designation === "fund_manager" && (entityTypes?.includes("manager") ?? false)
    ? "manager"
    : null;

// The record types that send documents and so may keep their passwords.
const DOCUMENT_PASSWORD_KINDS: EntityKind[] = ["manager", "vehicle", "company"];

// The designations a field of this shape could carry, in display order, or an
// empty list when none fits.
//
// The offer is SLOT-based for every designation but the email address. The
// role has two slots — TITLE and EMPLOYER — and a control is offered only
// while every slot it would claim is free:
//
//   - "Role" (a combined-shape field) claims both slots;
//   - "Role - Title" (a text field) claims TITLE, which any person_role
//     holder fills;
//   - "Role - Employer" (a pure link field) claims EMPLOYER, which a
//     person_employer holder fills — or a combined-shape person_role holder,
//     since that field carries the employer too.
//
// The forward and inverse management designations each have their own
// single slot, on vehicle and manager fields respectively. There is no
// tick-elsewhere-to-move gesture: moving a designation is untick, save, tick
// the other field, save.
//
// "Email address" (#13069) and "Phone number" (#11277) claim no slot: every
// person text field is offered both, whatever its siblings or the field itself
// carry, after the role offer.
//
// "Document password" (#14151) claims no slot either: every text field of a
// manager, vehicle or company is offered it, last, however many siblings carry
// it. A text field that also links to records is not offered it: a password
// links to nothing, and such a field keeps its management offer alone.
//
// `persistedDesignation` is what the field already carries: its own control
// always renders (ticked), both so it can be unticked and so the drift state
// — a schema save turns the split role field combined while an employer half
// stands — never shows a badge whose modal has lost all trace of it.
// `siblingFacts` is the OTHER fields of the same record type, as persisted.
//
// The server's designation endpoint accepts strictly more than this offers
// (it keeps move-and-subsume semantics as the single-holder safety net); the
// UI being the stricter side is the safe drift direction.
export const offerableDesignations = (
  entityKind: EntityKind,
  entityTypes: SchemaFact["entity_types"],
  scalarType: SchemaFact["scalar_type"],
  siblingFacts: Pick<
    SchemaFact,
    "designates" | "entity_types" | "scalar_type"
  >[],
  persistedDesignation: AttributeDesignation | null = null
): AttributeDesignation[] => {
  if (
    DOCUMENT_PASSWORD_KINDS.includes(entityKind) &&
    scalarType === "string" &&
    !entityTypes?.length
  ) {
    return ["document_password"];
  }
  if (entityKind === "vehicle" && entityTypes?.includes("manager")) {
    if (persistedDesignation === "fund_manager") {
      return ["fund_manager"];
    }
    const managerTaken = siblingFacts.some(
      (f) => f.designates === "fund_manager"
    );
    return managerTaken ? [] : ["fund_manager"];
  }
  if (entityKind === "manager" && entityTypes?.includes("vehicle")) {
    if (persistedDesignation === "manages_vehicles") {
      return ["manages_vehicles"];
    }
    const vehiclesTaken = siblingFacts.some(
      (f) => f.designates === "manages_vehicles"
    );
    return vehiclesTaken ? [] : ["manages_vehicles"];
  }
  if (entityKind === "person") {
    const titleTaken = siblingFacts.some((f) => f.designates === "person_role");
    const employerTaken = siblingFacts.some(
      (f) =>
        f.designates === "person_employer" ||
        (f.designates === "person_role" &&
          combinedRoleShape(f.scalar_type, f.entity_types))
    );
    if (scalarType === "string") {
      const claimsBothSlots = combinedRoleShape(scalarType, entityTypes);
      const roleOffered =
        persistedDesignation === "person_role" ||
        !(titleTaken || (claimsBothSlots && employerTaken));
      return roleOffered
        ? ["person_role", "person_email", "person_phone"]
        : ["person_email", "person_phone"];
    }
    if (!scalarType && linksToEmployer(entityTypes)) {
      if (persistedDesignation === "person_employer") {
        return ["person_employer"];
      }
      return employerTaken ? [] : ["person_employer"];
    }
  }
  return [];
};
